/** Display the proposed operation, using its captured tool declaration and actual policy reason. */

import type { ApprovalCard, CapabilityEntry, ReplyOption } from "../lib/api";
import type { Tone } from "../lib/format";
import { channelLabels, declaredValue } from "./declaredReview";
import { channelWords, fileName, machineValue, moneyWords, reachWords } from "./words";

/** What a tool declared for the owner, or the empty shape when it has not. */
export interface ToolFacts {
  /** `ToolSpec.owner.act`: what it does, in one sentence. "" when undeclared. */
  act: string;
  /** `ToolSpec.owner.params`: argument name -> the name the card shows. */
  labels: Record<string, string>;
  parameters?: Record<string, unknown>;
  review?: Record<string, unknown>;
  holder?: { plugin: string; label: string };
  channels?: Record<string, string>;
}

export type ToolIndex = Record<string, ToolFacts>;

/** A closed request is not evidence that a person answered or a tool ran. */
export function approvalResolution(card: ApprovalCard): { label: string; tone: Tone } {
  if (!card.resolved) return { label: card.resolution ? "答复已送出，等待确认" : "等你决定", tone: "warning" };
  // Only a structured outcome establishes expiry; reason text is not a code.
  if (card.resolution === "expired" || card.failure?.code === "expired") {
    return { label: "等待已到期", tone: "unknown" };
  }
  if (!card.decided_by && !card.answered_from) {
    return { label: "请求已结束，未记录人工答复", tone: "unknown" };
  }
  const answer = card.options.find(option => option.value === card.resolution)?.label
    || (card.resolution === "approve" ? "同意" : card.resolution === "deny" ? "拒绝" : "");
  return {
    label: answer ? `已答复：${answer}` : "已答复，结果未说明",
    tone: card.resolution === "approve" ? "good" : card.resolution === "deny" ? "critical" : "unknown",
  };
}

/** One line of "what, concretely", already in words. */
export interface AskLine {
  /** The label, or "" when the line is a whole sentence on its own. */
  label: string;
  value: string;
  /**
   * The value is the model's own words, shown as it wrote them.
   *
   * The vocabulary gate skips these (`components/owner.tsx`, `LineRow`): a
   * model that writes a tool key into the sentence it wants remembered has put
   * that key in front of the owner, and the page's job is to show what it is
   * about to agree to -- not a tidied version of it.
   */
  quoted?: boolean;
}

export interface OwnerAsk {
  /** The headline: what it wants. */
  title: string;
  /** One line: why this needs a person. */
  why: string;
  /** The concrete thing being asked about. Empty when nothing could be said. */
  what: AskLine[];
  /** Behind 「细节」. Sentences, not fields. */
  detail: AskLine[];
  /**
   * How many arguments this call carried that no schema describes, so they are
   * counted and not shown. A number the page prints: silently dropping them
   * would be the page deciding an owner may not know something was left out.
   */
  unnamed: number;
  options: ReplyOption[];
  /**
   * True when the tool this is about declares nothing this shell can name --
   * the page says so plainly, and it is the finding that says which plugin has
   * to supply a description.
   */
  unnamedTool: boolean;
}

/**
 * Each tool's owner-facing declaration, keyed by tool.
 *
 * Read off the `tools` registry as `/api/capabilities` returns it -- a registry
 * row's spec crosses whole (`workbench/server/capabilities.py` `_entry`), so
 * this reads `spec.owner` and nothing else about the tool. A registry that is
 * not there, or a tool that declared nothing, yields the empty shape and every
 * card reports missing information without inferring it from a category.
 *
 * `entry.title` is not read either. A row's title is only a name its plugin
 * gave it, and `ToolSpec` has no field for one, so on a real core a tool's is
 * always empty -- a test fixture once wrote `display_name` and was the only
 * place a tool ever had one (D921).
 */
export function toolIndex(registries: { kind: string; entries: CapabilityEntry[]; problem?: string | null }[]): ToolIndex {
  const tools = registries.find((registry) => registry.kind === "tools");
  const out: ToolIndex = {};
  const channels = channelLabels(registries as import("../lib/api").CapabilityRegistry[]);
  for (const entry of tools?.problem ? [] : tools?.entries ?? []) {
    const owner = object(object(entry.spec ?? {}).owner);
    const review = object(object(entry.spec.metadata).owner_review);
    const parameters = object(entry.spec.parameters);
    out[entry.key] = { act: text(owner.act).trim(), labels: paramLabels(owner.params),
      ...(Object.keys(review).length ? { review } : {}),
      ...(Object.entries(object(parameters.properties)).some(([key, field]) => object(owner.params)[key] &&
        (["array", "object"].includes(text(object(field).type)) || object(field).writeOnly === true || object(field).secret === true)) ||
        Array.isArray(review.verbatim_params) ? { parameters } : {}),
      ...(typeof review.holder_label === "string" ? { holder: { plugin: entry.plugin, label: review.holder_label } } : {}),
      ...(Object.keys(channels).length ? { channels } : {}),
    };
  }
  return out;
}

/**
 * What 确认规则 says when no policy declared one.
 *
 * The shell's own sentence, and general on purpose: it states the one thing
 * that is certainly true of every card -- the request is waiting on a person --
 * without inventing a rule nobody wrote.
 */
export const UNDECLARED_REASON = "这一步要先经过你同意。";

/** Render the captured proposal. Wording belongs to its author and holding policy. */
export function ownerAsk(card: ApprovalCard, tools: ToolIndex): OwnerAsk {
  const captured = card.review?.presentation;
  const facts = captured ? toolIndex([{ kind: "tools", entries: [{
    key: card.tool || "", plugin: card.held_by, spec: captured,
  } as CapabilityEntry] }])[card.tool || ""] : tools[card.tool ?? ""] ?? null;
  const reading = argumentLines(card.arguments, facts);
  const title = card.review?.title || facts?.act || "确认这次操作";
  const what = reading.lines;
  if (card.review?.summary) what.unshift({ label: "", value: card.review.summary, quoted: true });
  const detail: AskLine[] = [];
  // Why this stopped here, in the words of the policy that stopped it --
  // `policy_reason` off `metadata.approval_projection`, which is a sentence
  // declared for the person being asked.
  //
  // Never `card.detail`. That is the holder's line on the effect row, written
  // for whoever reads the ledger: the core's own default is `held by a gate
  // consumer` and a fixture's was `waiting on a person`, both of which stood
  // under 确认规则 on an Owner card in a real browser (r26a pinned the second).
  // A hold with no declared reason is not a reason the shell may go and find
  // somewhere else; it is a hold with no reason, and what is true then is the
  // rule the shell does own -- that this needs the person (§7.183).
  const why = card.review?.policy_reason || UNDECLARED_REASON;
  detail.push({ label: "确认规则", value: why, quoted: Boolean(card.review?.policy_reason) });
  const reach = reachWords(card.level);
  if (reach) detail.push({ label: "影响范围", value: reach });
  return {
    title, why, what, detail,
    unnamed: countUnnamed(card, facts, reading.accounted || what.length),
    options: card.options,
    unnamedTool: !facts?.act && !card.review?.title,
  };
}

/** What `argumentLines` read, and how many of the arguments it accounts for. */
interface ArgumentReading {
  lines: AskLine[];
  accounted: number;
}

/** A number and nothing else: the only value a currency can be the unit of. */
const BARE_NUMBER = /^\d+([.,]\d+)*$/;

/**
 * Every argument whose schema gives it a Chinese name *and* a readable value.
 *
 * Scalars only. A nested object rendered as text is JSON on the page under
 * another name, and there is no schema label for its insides.
 *
 * A described label is not a promise about the value underneath it (D431): the
 * owner's card carried `来源引用：<a tool key>` and `layer='fact'` because the
 * schema described those two arguments in Chinese, and the page printed whatever
 * they held. A value that is a machine's name for something is counted with the
 * others it could not say, which is what `unnamed` has always been for -- so the
 * card still admits that something was left out instead of quietly shrinking.
 */
function argumentLines(
  args: Record<string, unknown>,
  facts: ToolFacts | null,
): ArgumentReading {
  const out: AskLine[] = [];
  /** Arguments the lines above account for, including a unit folded into one. */
  let accounted = 0;
  let unit = "";
  for (const [key, value] of Object.entries(args)) {
    // The declared name, as declared. It used to be the first clause of the
    // parameter's schema `description`, which is the model's text: 「producer_video
    // 的运行目录」 became 「producer_vid…」 on a payment card (D662) and 「设 true
    // 实际访问 Google 核验已有凭据」 became an argument's name on another (D780).
    // An argument the declaration does not name is counted, which is what
    // `unnamed` says.
    const label = facts?.labels[key] ?? "";
    if (!label) continue;
    const property = object(object(facts?.parameters?.properties)[key]);
    if (property.writeOnly === true || property.secret === true) continue;
    if (Array.isArray(facts?.review?.verbatim_params) && facts.review.verbatim_params.includes(key)) {
      const literal = scalar(value);
      if (literal !== null) { out.push({ label, value: literal, quoted: true }); accounted += 1; }
      continue;
    }
    const nested = value && typeof value === "object" ? declaredValue(value, property) : null;
    const said = nested || scalar(value);
    if (said === null) continue;
    if (nested) { out.push({ label, value: nested, quoted: true }); accounted += 1; continue; }
    // A value this shell already has a word for is that word. Asked of the
    // value, not of the argument's name: the dictionary holds three channel keys
    // and nothing else a tool would pass, and the claim card's
    // 「管理渠道：workbench」 is the one place it matters -- `workbench` is on the
    // gate's own banned list, and 「这台电脑上的这一页」 is what it means.
    const named = channelWords(said, facts?.channels);
    if (named) {
      out.push({ label, value: named });
      accounted += 1;
      continue;
    }
    // A currency is the unit of the amount on this same card, not a fact of its
    // own: held back here and joined to the number below (D663).
    const money = moneyWords(said);
    if (money) {
      unit = money;
      accounted += 1;
      continue;
    }
    // A path is this system's bookkeeping; the file at the end of it is what the
    // Owner is being asked about.
    const file = fileName(said);
    if (file) {
      out.push({ label, value: file, quoted: true });
      accounted += 1;
      continue;
    }
    if (machineValue(said)) continue;
    // The value is what the model asked for, word for word.
    out.push({ label, value: said, quoted: true });
    accounted += 1;
  }
  // One number and one currency: the unit belongs on that number. Two numbers
  // and the card cannot know which one the currency counts, so it says neither
  // wrongly -- the amount keeps its declared label and the unit stays off.
  const numbers = out.filter((line) => BARE_NUMBER.test(line.value));
  if (unit && numbers.length === 1) numbers[0].value = `${numbers[0].value} ${unit}`;
  else if (unit) accounted -= 1;
  return { lines: out, accounted };
}

/** Arguments this call carried that the page could not say in words. */
function countUnnamed(
  card: ApprovalCard,
  facts: ToolFacts | null,
  shown: number,
): number {
  void facts;
  return Math.max(0, Object.keys(card.arguments).length - shown);
}

/** `owner.params` as a plain string map; anything else declared is dropped. */
function paramLabels(params: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(object(params))) {
    const label = text(value).trim();
    if (label) out[key] = label;
  }
  return out;
}

/** The value as a person would read it, or null when it is not one line. */
function scalar(value: unknown): string | null {
  if (typeof value === "string") return value.trim() || null;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value === "boolean") return value ? "是" : "否";
  return null;
}

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function object(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}
